/**
 * ADR-0046 Slice 4 — ambient `.d.ts` for an app's declared external-JS-library
 * globals, so the builder code editors (binding fields, JS Objects, `run_js`,
 * custom components) AUTOCOMPLETE `_.`, `dayjs(…).`, … exactly like they do for
 * `$page` / `$queries` / JS Objects.
 *
 * The page bindings `.d.ts` (`pagePreview.buildPageBindingsDts`) declares the
 * scope roots; this module adds one `declare const <global>: <Type>` per library.
 * For a CURATED set of well-known libraries we ship a useful member surface; an
 * unknown library falls back to `any` (no autocomplete, but never a false error).
 * The `global` is operator-CHOSEN, so the curated type is keyed by the npm NAME
 * and the actual global name is substituted in — a library aliased to a custom
 * global still types correctly.
 */

/** The minimum a declared library carries for typing (a subset of `JsLibraryOut`). */
export interface LibRef {
  name: string;
  global: string;
}

/** Curated member surfaces, keyed by npm package name (lower-cased). Each entry
 *  is a factory taking the resolved global name → its `declare const` line(s).
 *  Kept broad with an `[key: string]: any` index so anything we didn't enumerate
 *  still resolves (no red squiggle), while the common calls autocomplete. */
const CURATED: Record<string, (g: string) => string> = {
  lodash: (g) =>
    `declare const ${g}: {
  chunk(a: any[], size?: number): any[][];
  compact(a: any[]): any[];
  cloneDeep<T>(v: T): T;
  debounce<T extends (...a: any[]) => any>(fn: T, ms?: number): T;
  filter(a: any, pred: any): any[];
  find(a: any, pred: any): any;
  get(obj: any, path: any, def?: any): any;
  groupBy(a: any[], by: any): Record<string, any[]>;
  isEmpty(v: any): boolean;
  keyBy(a: any[], by: any): Record<string, any>;
  map(a: any, by: any): any[];
  maxBy(a: any[], by: any): any;
  meanBy(a: any[], by: any): number;
  minBy(a: any[], by: any): any;
  orderBy(a: any[], by: any, orders?: any): any[];
  range(start: number, end?: number, step?: number): number[];
  set(obj: any, path: any, value: any): any;
  sortBy(a: any[], by: any): any[];
  sumBy(a: any[], by: any): number;
  uniqBy(a: any[], by: any): any[];
  [key: string]: any;
};`,
  dayjs: (g) =>
    // dayjs is CALLABLE and chainable; the chain returns are `any` (a recursive
    // named interface would collide if a second lib reused the global), but the
    // call signature + static methods type, so `${g}(x).format(…)` resolves.
    `declare const ${g}: {
  (input?: any, format?: any): any;
  unix(t: number): any;
  extend(plugin: any, option?: any): void;
  utc?(input?: any): any;
  locale(preset?: any): string;
  [key: string]: any;
};`,
  numeral: (g) =>
    `declare const ${g}: {
  (value?: any): { format(fmt?: string): string; value(): number; add(n: number): any; subtract(n: number): any };
  [key: string]: any;
};`,
  uuid: (g) =>
    `declare const ${g}: { v4(): string; v1(): string; validate(s: string): boolean; [key: string]: any };`,
  validator: (g) =>
    `declare const ${g}: {
  isEmail(s: string): boolean;
  isURL(s: string, opts?: any): boolean;
  isUUID(s: string): boolean;
  isNumeric(s: string): boolean;
  trim(s: string): string;
  [key: string]: any;
};`,
};

/**
 * Build the ambient declarations for an app's libraries. Returns "" when there
 * are none (a harmless blank line in the assembled `.d.ts`). De-dupes by global
 * (the same window global can't be declared twice), and skips malformed entries.
 */
export function libraryGlobalsDts(libs: LibRef[] | undefined): string {
  const seen = new Set<string>();
  const lines: string[] = [];
  for (const lib of libs ?? []) {
    if (!lib?.global || !lib?.name || seen.has(lib.global)) continue;
    seen.add(lib.global);
    const make = CURATED[lib.name.toLowerCase()];
    lines.push(make ? make(lib.global) : `declare const ${lib.global}: any;`);
  }
  if (lines.length === 0) return "";
  return ["", "// External JS libraries (ADR-0046) declared on this app.", ...lines].join("\n");
}
